import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface CaptchaProps {
  /** Chamado com o token quando resolvido, ou null quando expira/reseta. */
  onChange: (token: string | null) => void;
}

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const DEV_TOKEN = 'dev-captcha-bypass';
const SCRIPT_ID = 'recaptcha-v2-script';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    grecaptcha?: any;
    __onRecaptchaLoad?: () => void;
  }
}

/** Carrega o script do reCAPTCHA uma única vez e resolve quando pronto. */
function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha?.render) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('recaptcha load error')));
      return;
    }
    window.__onRecaptchaLoad = () => resolve();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('recaptcha load error'));
    document.head.appendChild(script);
  });
}

/**
 * Verificação anti-abuso via Google reCAPTCHA v2 (checkbox).
 *
 * Sem `VITE_RECAPTCHA_SITE_KEY` configurado, opera em modo de desenvolvimento:
 * emite um token fixo automaticamente para permitir o fluxo local. O backend
 * é a autoridade final na verificação do token.
 */
export function Captcha({ onChange }: CaptchaProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  // Mantém o callback mais recente sem re-renderizar o widget.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    // Modo dev: sem site key, libera automaticamente.
    if (!SITE_KEY) {
      onChangeRef.current(DEV_TOKEN);
      return;
    }

    let cancelled = false;
    loadRecaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(null),
          'error-callback': () => onChangeRef.current(null),
        });
      })
      .catch(() => {
        // Falha ao carregar o script — não bloqueia silenciosamente.
        onChangeRef.current(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!SITE_KEY) {
    return (
      <div
        className="mb-4 py-[10px] px-[15px] rounded-[8px] bg-[#fff8e1] border border-solid border-[#f1c40e] text-[11px] text-[#8a6d00]"
        role="status"
      >
        <i className="fa fa-shield mr-2" aria-hidden="true"></i>
        {t('views.registration.captcha_dev_mode', 'Verificação anti-robô em modo de desenvolvimento (captcha desativado).')}
      </div>
    );
  }

  return <div ref={containerRef} className="mb-4 flex justify-center" />;
}
